<script setup lang="ts">
// The trajectory: the trailing six-week growth rate for every week of the
// season, this season as bars from zero against the five seasons before it as
// one shaded range. The bars are the reading — below zero the population is
// falling — and the band is what the same fit found in the same weeks of the
// five seasons before, so a bar outside the band is the signal. No prior season
// is drawn as its own line: five lines over 53 weeks would hide the bars they
// are there to frame.
import { computed } from "vue";

import type { MapReviewPopulationSurveillance } from "@/type/api";

import BarChart from "../charts/BarChart.vue";
import type { BarDataset, BarOverlayDataset } from "../charts/theme";
import { OKABE_ITO, withAlpha } from "../charts/theme";

const props = defineProps<{
	surveillance: MapReviewPopulationSurveillance;
}>();

const BAND_COLOR = "#8a9098";

const labels = computed(() =>
	props.surveillance.weeks.map((week) => `W${week}`),
);

const currentName = computed(() => {
	const prior = props.surveillance.prior;
	return prior.length > 0
		? String(prior[prior.length - 1].year + 1)
		: "This season";
});

/** The prior seasons' week-by-week range: the lowest and highest rolling slope
 * any of them carried that week. A week no prior season reached has neither
 * edge, and the band simply breaks there. */
const envelope = computed(() => {
	const rows = props.surveillance.rolling.prior.map((season) => season.series);
	const length = props.surveillance.weeks.length;
	const lo: (number | null)[] = [];
	const hi: (number | null)[] = [];
	for (let index = 0; index < length; index++) {
		let lowest: number | null = null;
		let highest: number | null = null;
		for (const row of rows) {
			const value = row[index];
			if (value === null || value === undefined) continue;
			if (lowest === null || value < lowest) lowest = value;
			if (highest === null || value > highest) highest = value;
		}
		lo.push(lowest);
		hi.push(highest);
	}
	return { lo, hi };
});

const hasValues = computed(() =>
	[
		...props.surveillance.rolling.current,
		...props.surveillance.rolling.priorAvg,
	].some((value) => value !== null),
);

const datasets = computed<(BarDataset | BarOverlayDataset)[]>(() => {
	const rolling = props.surveillance.rolling;
	const band = envelope.value;
	return [
		// The band's upper edge, filling down to the lower edge at the next
		// index. Both edges are transparent strokes: the band is the ink.
		{
			type: "line" as const,
			label: "Five-season range",
			data: band.hi,
			borderColor: "transparent",
			backgroundColor: withAlpha(BAND_COLOR, 0.18),
			borderWidth: 0,
			pointRadius: 0,
			fill: "+1",
			band: true,
		},
		{
			type: "line" as const,
			label: "Five-season range floor",
			data: band.lo,
			borderColor: "transparent",
			backgroundColor: "transparent",
			borderWidth: 0,
			pointRadius: 0,
			fill: false,
			band: true,
		},
		{
			type: "line" as const,
			label: "Five-season average",
			data: rolling.priorAvg,
			borderColor: OKABE_ITO[4],
			backgroundColor: "transparent",
			borderWidth: 1.2,
			borderDash: [4, 3],
			pointRadius: 0,
			pointHoverRadius: 3,
			fill: false,
		},
		// The bars last, so they sit on top of the band.
		{
			type: "bar" as const,
			label: currentName.value,
			data: rolling.current as number[],
			backgroundColor: OKABE_ITO[0],
			borderColor: OKABE_ITO[0],
			borderWidth: 0,
			barPercentage: 0.9,
			categoryPercentage: 0.95,
		},
	];
});
</script>

<template>
	<section class="population-trend">
		<p class="panel-title">Trajectory</p>
		<p class="panel-help">
			Weekly growth rate (%), the trailing six-week log-linear fit. Bars are
			this season; the shaded range is the five seasons before it at the same
			week, with their average dashed through it. Bars below zero are weeks the
			population was falling.
		</p>
		<p v-if="!hasValues" class="panel-empty">
			No week has a six-week window with enough sampled weeks to fit a slope.
		</p>
		<div v-else class="panel-chart">
			<BarChart
				:labels="labels"
				:datasets="datasets"
				:show-legend="false"
				zero-baseline
				tick-suffix="%"
			/>
		</div>
		<div v-if="hasValues" class="legend">
			<span class="key"
				><span class="swatch bar" :style="{ background: OKABE_ITO[0] }" />{{
					currentName
				}}</span
			>
			<span class="key"
				><span
					class="swatch"
					:style="{ background: withAlpha(BAND_COLOR, 0.35) }"
				/>Five-season range</span
			>
			<span class="key"
				><span
					class="swatch dashed"
					:style="{ borderTopColor: OKABE_ITO[4] }"
				/>Five-season average</span
			>
		</div>
	</section>
</template>

<style scoped>
.population-trend {
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	padding: 8px 10px 10px;
	min-width: 0;
	/* The pane's slot is a cell grid; this panel reads as one column of the
	   rail, in the order the question asks. */
	grid-column: 1 / -1;
}

.panel-title {
	margin: 0 0 2px;
	font-size: 0.78rem;
	font-weight: 600;
	color: #212529;
}

.panel-help {
	margin: 0 0 8px;
	font-size: 0.68rem;
	line-height: 1.35;
	color: #6c757d;
}

.panel-empty {
	margin: 0;
	font-size: 0.72rem;
	color: #6c757d;
}

.panel-chart {
	position: relative;
	height: 180px;
}

.legend {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	margin-top: 6px;
	font-size: 0.66rem;
	color: #6c757d;
}

.key {
	display: flex;
	align-items: center;
	gap: 5px;
}

.swatch {
	width: 14px;
	height: 3px;
	border-radius: 2px;
}

.swatch.bar {
	height: 8px;
	border-radius: 1px;
}

.swatch.dashed {
	height: 0;
	border-top-width: 2px;
	border-top-style: dashed;
	background: transparent;
}
</style>
